import { useEffect, useState } from 'preact/hooks';
import {
  Calendar as CalendarIcon,
  LogOut,
  Trash2,
  Plus,
  User as UserIcon,
  Users,
  Palette,
  Baby,
} from 'lucide-preact';
import { api, type Child, type FamilySettings, type Me } from '../lib/api.js';

type Props = {
  me: Me;
  onUpdate: (m: Me) => void;
};

export function Settings({ me, onUpdate: _onUpdate }: Props) {
  const [settings, setSettings] = useState<FamilySettings | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newChildName, setNewChildName] = useState('');
  const [newChildCalId, setNewChildCalId] = useState('');
  const [calendars, setCalendars] = useState<Array<{ id: string; name: string; isDefault: boolean; canEdit: boolean }>>([]);

  async function load() {
    try {
      const [s, c] = await Promise.all([
        api<FamilySettings>('/api/v1/family-settings'),
        api<Child[]>('/api/v1/children'),
      ]);
      setSettings(s);
      setChildren(c);
    } catch (e) {
      setError(String(e));
    }
    try {
      const cals = await api<Array<{ id: string; name: string; isDefault: boolean; canEdit: boolean }>>('/api/v1/graph/calendars');
      setCalendars(cals.filter((c) => !c.isDefault));
    } catch {
      // ignore
    }
  }

  useEffect(() => { load(); }, []);

  async function logout() {
    try {
      await api('/auth/logout', { method: 'POST' });
      window.location.href = '/auth/login';
    } catch (e) {
      setError(String(e));
    }
  }

  async function addChild(e: Event) {
    e.preventDefault();
    try {
      await api<Child>('/api/v1/children', {
        method: 'POST',
        body: JSON.stringify({ name: newChildName, graphCalendarId: newChildCalId }),
      });
      setNewChildName('');
      setNewChildCalId('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function deleteChild(id: string) {
    if (!confirm('Kind löschen?')) return;
    try {
      await api(`/api/v1/children/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <div class="screen">
      <h1>Einstellungen</h1>
      {error ? <div class="error">{error}</div> : null}

      <h2 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <UserIcon size={18} /> Du
      </h2>
      <div class="card">
        <div><strong>{me.displayName}</strong></div>
        <div class="muted">{me.email}</div>
      </div>

      <h2 style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Users size={18} /> Familie
      </h2>
      {settings ? (
        <div class="card">
          <div><strong>{settings.logoEmoji ?? '👨‍👩‍👧‍👦'} {settings.familyName}</strong></div>
          <div class="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Palette size={14} /> Akzent:
            <span class="person-color" style={{ background: settings.accentColor }} />
            {settings.accentColor}
          </div>
        </div>
      ) : null}

      <h2 style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Baby size={18} /> Kinder
      </h2>
      <div class="stack">
        {children.map((c) => {
          const cal = calendars.find((x) => x.id === c.graphCalendarId);
          return (
            <div class="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div>
                  <span class="person-color" style={{ background: c.color }} />
                  <strong>{c.name}</strong>
                </div>
                <div class="muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CalendarIcon size={12} />
                  {cal ? cal.name : 'Outlook-Kalender'}
                </div>
              </div>
              <button class="ghost" onClick={() => deleteChild(c.id)} style={{ flexShrink: 0 }} aria-label="Löschen">
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>

      <form onSubmit={addChild} style={{ marginTop: 14 }}>
        <h3 style={{ fontSize: 14, color: 'var(--c-muted)' }}>Kind hinzufügen</h3>
        <div class="field">
          <input required value={newChildName} onInput={(e) => setNewChildName((e.target as HTMLInputElement).value)} placeholder="Name" />
        </div>
        <div class="field">
          {calendars.length > 0 ? (
            <select required value={newChildCalId} onInput={(e) => setNewChildCalId((e.target as HTMLSelectElement).value)}>
              <option value="" disabled>Outlook-Kalender wählen</option>
              {calendars.map((c) => (
                <option value={c.id}>
                  {c.name}{c.canEdit ? '' : ' (read-only)'}
                </option>
              ))}
            </select>
          ) : (
            <input required value={newChildCalId} onInput={(e) => setNewChildCalId((e.target as HTMLInputElement).value)} placeholder="Graph-Calendar-ID (konnte nicht automatisch geladen werden)" />
          )}
        </div>
        <button type="submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Plus size={16} /> Hinzufügen
        </button>
      </form>

      <button class="ghost" onClick={logout} style={{ marginTop: 30, width: '100%', color: 'var(--c-danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <LogOut size={16} /> Abmelden
      </button>
    </div>
  );
}
